import React, {useState, useEffect} from 'react';

const MARKERS = ['A', 'B', 'C', 'D', 'E', 'F'];

const MultipleChoice = ({question, onSubmit, showFeedback, isCorrect}) => {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setSelected(null);
  }, [question]);

  const handleSelect = idx => {
    if (showFeedback) return;
    setSelected(idx);
  };

  const handleSubmit = () => {
    if (selected === null) return;
    onSubmit(question.options[selected]);
  };

  const getOptionClass = idx => {
    if (!showFeedback) {
      return idx === selected ? 'lcv-option-selected' : '';
    }
    if (question.options[idx] === question.correctAnswer) {
      return 'lcv-option-correct';
    }
    if (idx === selected) {
      return 'lcv-option-incorrect';
    }
    return '';
  };

  return (
    <>
      <div className="lcv-check-question">{question.question}</div>
      <div className="lcv-options">
        {question.options.map((opt, idx) => (
          <button
            key={idx}
            className={`lcv-option ${getOptionClass(idx)}`}
            onClick={() => handleSelect(idx)}
            disabled={showFeedback}>
            <span className="lcv-option-marker">{MARKERS[idx]}</span>
            <span>{opt}</span>
          </button>
        ))}
      </div>
      {!showFeedback && (
        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 16}}>
          <button
            className="lcv-btn-primary"
            onClick={handleSubmit}
            disabled={selected === null}>
            Submit
          </button>
        </div>
      )}
    </>
  );
};

const FillInTheBlank = ({question, onSubmit, showFeedback}) => {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setSelected(null);
  }, [question]);

  const handleSelect = word => {
    if (showFeedback) return;
    setSelected(word);
  };

  const handleSubmit = () => {
    if (!selected) return;
    onSubmit(selected);
  };

  const displayQuestion = question.question.replace('______', selected || '______');

  return (
    <>
      <div className="lcv-check-question">{displayQuestion}</div>
      <div className="lcv-word-bank">
        <div className="lcv-wb-label">Word Bank</div>
        <div className="lcv-wb-chips">
          {question.wordBank.map((word, idx) => {
            let chipClass = '';
            if (showFeedback && word === question.correctAnswer) {
              chipClass = 'lcv-wb-chip-correct';
            } else if (selected === word) {
              chipClass = showFeedback ? '' : 'lcv-wb-chip-selected';
            }
            return (
              <button
                key={idx}
                className={`lcv-wb-chip ${chipClass}`}
                onClick={() => handleSelect(word)}
                disabled={showFeedback}>
                {word}
              </button>
            );
          })}
        </div>
      </div>
      {!showFeedback && (
        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 16}}>
          <button
            className="lcv-btn-primary"
            onClick={handleSubmit}
            disabled={!selected}>
            Submit
          </button>
        </div>
      )}
    </>
  );
};

const Matching = ({question, onSubmit, showFeedback}) => {
  const [matches, setMatches] = useState({});
  const [selectedLeft, setSelectedLeft] = useState(null);

  useEffect(() => {
    setMatches({});
    setSelectedLeft(null);
  }, [question]);

  const handleLeftClick = leftVal => {
    if (showFeedback) return;
    setSelectedLeft(selectedLeft === leftVal ? null : leftVal);
  };

  const handleRightClick = rightVal => {
    if (showFeedback || !selectedLeft) return;
    setMatches(prev => ({...prev, [selectedLeft]: rightVal}));
    setSelectedLeft(null);
  };

  const handleSubmit = () => {
    const answerStr = question.matchPairs
      .map(p => `${p.left}-${matches[p.left] || ''}`)
      .join(', ');
    onSubmit(answerStr);
  };

  const allMatched =
    question.matchPairs.length > 0 &&
    question.matchPairs.every(p => matches[p.left]);

  const rightValues = question.matchPairs.map(p => p.right);
  const usedRight = new Set(Object.values(matches));

  return (
    <>
      <div className="lcv-check-question">{question.question}</div>
      <div className="lcv-matching">
        <div className="lcv-matching-hint">Match each item on the left with its pair on the right</div>
        {question.matchPairs.map((pair, idx) => {
          const matched = matches[pair.left];
          return (
            <div key={idx} className="lcv-match-row">
              <button
                className="lcv-match-left"
                onClick={() => handleLeftClick(pair.left)}
                style={{
                  outline: selectedLeft === pair.left ? '2px solid #42A5F5' : 'none',
                  cursor: showFeedback ? 'default' : 'pointer',
                  background: selectedLeft === pair.left ? 'rgba(66,165,245,0.15)' : undefined,
                }}
                disabled={showFeedback}>
                {pair.left}
              </button>
              <div className="lcv-match-connector">→</div>
              <div className="lcv-match-right">
                {matched || (
                  <span style={{color: 'rgba(255,255,255,0.2)', fontStyle: 'italic'}}>
                    Select...
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {!showFeedback && selectedLeft && (
          <div style={{marginTop: 8}}>
            <div className="lcv-wb-label">Select the match for: {selectedLeft}</div>
            <div className="lcv-wb-chips">
              {rightValues
                .filter(r => !usedRight.has(r) || matches[selectedLeft] === r)
                .map((r, idx) => (
                  <button
                    key={idx}
                    className="lcv-wb-chip"
                    onClick={() => handleRightClick(r)}>
                    {r}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
      {!showFeedback && (
        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 16}}>
          <button
            className="lcv-btn-primary"
            onClick={handleSubmit}
            disabled={!allMatched}>
            Submit
          </button>
        </div>
      )}
    </>
  );
};

const DragAndDropPlaceholder = ({question}) => {
  return (
    <>
      <div className="lcv-check-question">{question.question}</div>
      <div className="lcv-dnd-placeholder">
        [Drag-and-drop — coming soon]
        <div style={{marginTop: 8, fontSize: 13, color: 'rgba(255,255,255,0.3)'}}>
          Correct answer: {question.correctAnswer}
        </div>
      </div>
    </>
  );
};

const QuestionRenderer = ({question, onSubmit, showFeedback, isCorrect}) => {
  if (!question) return null;

  if (question.deferred || question.type === 'drag_and_drop') {
    return <DragAndDropPlaceholder question={question} />;
  }

  switch (question.type) {
    case 'multiple_choice':
      return (
        <MultipleChoice
          question={question}
          onSubmit={onSubmit}
          showFeedback={showFeedback}
          isCorrect={isCorrect}
        />
      );
    case 'fill_in_the_blank':
      return (
        <FillInTheBlank
          question={question}
          onSubmit={onSubmit}
          showFeedback={showFeedback}
          isCorrect={isCorrect}
        />
      );
    case 'matching':
      return (
        <Matching
          question={question}
          onSubmit={onSubmit}
          showFeedback={showFeedback}
          isCorrect={isCorrect}
        />
      );
    default:
      return (
        <div className="lcv-dnd-placeholder">
          Unsupported question type: {question.type}
        </div>
      );
  }
};

export default QuestionRenderer;
